/**
 * The match video at the bottom of the calibration review page.
 *
 * The full VideoPlayer from the match page, deliberately inert: no canvas,
 * no telestration, no overlay actions — the reviewer just needs to WATCH
 * (jersey colours, who kicked off which way) while making decisions above.
 * Pop out opens the video in a REAL browser window (Ben, 2026-09-12) so it
 * can sit on another screen while the review page is worked — and, while
 * it is open, the Game timing card's step / speed / Mark buttons drive it
 * (Ben, 2026-10-14).
 *
 * The player is handed a presigned URL fetched via /media's ?url=1 shape
 * (the Clip Finder pattern): the <video> element cannot follow the 302
 * cross-origin in dev (Vite proxy + R2 CORS).
 */
import { useEffect, useState, type MutableRefObject, type Ref } from 'react'

import { api } from '../../lib/api'
import type { SyncState } from '../../lib/viz/frameTime'
import { VideoPlayer, type VideoPlayerHandle } from '../match/VideoPlayer'
import '../match/match-summary.css'

const noop = () => {}

export default function CalibrationVideo(
  { jobId, gameCode, fps, playerRef, popoutRef }: {
    jobId: string; gameCode: string; fps: number | null
    /** The player's handle, for the Game timing card's Mark / Go / step
     *  buttons (its getVideo). */
    playerRef?: Ref<VideoPlayerHandle>
    /** Set to the pop-out window's <video> while it is open, so the Game
     *  timing buttons drive the window the reviewer is watching. */
    popoutRef?: MutableRefObject<HTMLVideoElement | null>
  },
) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    api<{ url: string }>(`/media/job/${jobId}/video?url=1`)
      .then((d) => { if (live) setVideoUrl(d.url) })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [jobId])

  if (failed) return null // no video is a normal state, not an error banner

  const popOut = () => {
    if (!videoUrl) return
    // A blank window of OUR origin with a <video> written into it, not the
    // presigned URL opened directly: the review page must reach that video
    // to drive it from the Game timing card (Ben, 2026-10-14). A window
    // showing the R2 URL is another origin and cannot be touched.
    const w = window.open('', `cal-video-${jobId}`, 'width=1280,height=760')
    if (!w) return
    const inline = document.querySelector<HTMLVideoElement>('.cal-video__stage video')
    const d = w.document
    d.title = `${gameCode} · match video`
    d.body.style.cssText = 'margin:0;background:#000'
    d.body.replaceChildren()
    const v = d.createElement('video')
    v.src = videoUrl
    v.controls = true
    v.style.cssText = 'width:100vw;height:100vh;object-fit:contain'
    // carry on from where the page's player was, and stop that one
    if (inline) {
      v.currentTime = inline.currentTime
      inline.pause()
    }
    d.body.appendChild(v)
    if (popoutRef) {
      popoutRef.current = v
      w.addEventListener('pagehide', () => {
        if (popoutRef.current === v) popoutRef.current = null
      })
    }
  }

  const sync: SyncState = {
    videoGameStartTime: 0, videoHalftimeStartTime: null,
    videoHalftimeEndTime: null, videoFrameAligned: false, fps: fps ?? 30,
  }

  return (
    <div className="panel cal-card cal-video">
      <div className="cal-card__head">
        <h3 className="cal-h3">Match video</h3>
        <button type="button" className="adm-btn adm-btn--quiet"
                disabled={!videoUrl} onClick={popOut}>Pop out</button>
      </div>
      <div className="cal-video__stage">
        {videoUrl
          ? <VideoPlayer
              ref={playerRef}
              gameCode={gameCode}
              videoUrl={videoUrl}
              sync={sync}
              gameData={null}
              deepLinkFrame={null}
              showFrameCounter={false}
              canvas={null}
              hasTelestration={false}
              annotationMode={false}
              showDetectionBtn={false}
              telLayers={{}}
              detectionActive={false}
              highlightMode="off"
              highlightBall={false}
              highlightLinesVideo={false}
              highlightRingsSpatial={false}
              highlightLinesSpatial={false}
              deepLinkOverlays={null}
              onSpatialHotkey={noop}
              onManualMomentCreated={noop}
              onVideoAttached={noop}
              onSyncSaved={noop}
              onShareCurrentFrame={noop}
              showActions={false}
            />
          : <p className="cal-empty">Loading video…</p>}
      </div>
    </div>
  )
}
